import Image from "@/components/Image";
import Masonry from "@/components/Masonry";
import { getContentType } from "@/utils/getContentType";
import { getImageSrc2x } from "@/utils/imageUrl";
import classNames from "classnames";
import Link from "next/link";
import { memo, useCallback, useEffect, useState } from "react";
import AiTag from "../AiTag";
import HBIcon from "../HBIcon";
import MasonrySkeleton from "../Masonry/skeleton";
import styles from "./index.module.less";

function SimilarMaterialList({
  material_id,
  pin_id,
  onSelect,
  className = "",
  style = {},
  similiarList,
  isNew,
  isFull = false,
  searchWord,
  showSub = true,
  width = 116,
  columnNum = 3,
  gutter = 4,
  threshold = 10000,
  stackIndex = 0, // 新增：弹窗堆栈索引
  listClassName,
}) {
  // 根据 stackIndex 生成唯一的 scrollableTarget
  const scrollableTarget = `similarMaterialList-${stackIndex}`;
  const [visable, setVisable] = useState(true);
  const { data, error, loading } = similiarList;

  const render = useCallback(
    (pin) => {
      return (
        <div key={pin.pin_id}>
          <a
            className={styles.item}
            href={`/pins/${pin.pin_id}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey) {
                return;
              }
              e.preventDefault();
              const currentSrc =
                e.currentTarget.querySelector("img").currentSrc;
              onSelect(pin, currentSrc, "similarMaterial");
            }}
            data-gd-expose="content_expose"
            data-gd-click="material_click"
            data-module-name="相似商用素材"
            data-content-id={pin.pin_id}
            data-content-type={getContentType(pin)}
            data-material-id={pin.file_material?.material_id}
            data-board-id={pin.board_id}
            data-file-id={pin.file_id}
            data-search-word={searchWord}
            data-ext={JSON.stringify(pin?.ext)}
            data-aigc
          >
            <Image
              onLoad={(evt) => {
                const target = evt.target;
                target?.classList.add("transparent-img-bg");
              }}
              {...getImageSrc2x(pin.file, { width })}
              width={width}
              height={Math.round((pin.file?.height / pin.file?.width) * width)}
              alt={pin.raw_text}
              showError={true}
            />
            <AiTag pin={pin} size="sm" />
          </a>
        </div>
      );
    },
    [onSelect, width, searchWord]
  );

  const renderListData = data?.pins || [];
  useEffect(() => {
    if (!loading) {
      const renderListData = data?.pins || [];
      if (error || data?.err || renderListData.length === 0) {
        setVisable(false);
        if (error || data?.err) {
          console.error(error || data?.err);
        }
      } else {
        setVisable(true);
      }
    }
  }, [data, error, loading]);

  return (
    <>
      {visable && (
        <div
          className={classNames(
            className,
            isNew && styles.isNew,
            isFull && styles.isFull
          )}
          style={style}
        >
          <div className={styles.similar} data-content-source="相似商用素材">
            <div className={styles.header}>
              <Link href={`/pins/${pin_id}/similar/materials`}>
                <a
                  target={"_blank"}
                  className={classNames(styles.title, {
                    [styles.withIcon]: showSub,
                  })}
                  data-gd-click="button_click"
                  data-button-name="更多相似商用素材"
                  data-content-source="相似商用素材-标题"
                  data-material-id={material_id}
                  data-module-name="相似商用素材"
                  data-search-word={searchWord}
                  rel="nofollow"
                >
                  相似商用素材
                </a>
              </Link>
              {showSub ? (
                <span className={styles.sub}>源文件可下载编辑</span>
              ) : (
                <span style={{ flex: 1 }} />
              )}
              <Link href={`/pins/${pin_id}/similar/materials`}>
                <a
                  target={"_blank"}
                  className={styles.more}
                  data-gd-click="button_click"
                  data-button-name="更多相似商用素材"
                  data-content-source="相似商用素材-顶部"
                  data-material-id={material_id}
                  data-module-name="相似商用素材"
                  data-search-word={searchWord}
                  rel="nofollow"
                >
                  更多
                  <HBIcon type="ic_chevron_right" />
                </a>
              </Link>
            </div>
            <div
              id={scrollableTarget}
              className={classNames(styles.list, listClassName)}
            >
              {loading ? (
                <MasonrySkeleton
                  columnNum={columnNum}
                  columnSize={width}
                  gutter={gutter}
                  itemClassName={styles.skeletonItem}
                  hideText={true}
                  radius={0}
                />
              ) : (
                <>
                  <Masonry
                    bricks={renderListData}
                    brickId="pin_id"
                    scrollElement={scrollableTarget}
                    render={render}
                    columnNum={columnNum}
                    threshold={threshold}
                    gutter={gutter}
                    columnSize={width}
                  />
                  <Link href={`/pins/${pin_id}/similar/materials`}>
                    <a
                      target={"_blank"}
                      className={styles.footer}
                      data-gd-click="button_click"
                      data-button-name="更多相似商用素材"
                      data-content-source="相似商用素材-底部"
                      data-material-id={material_id}
                      data-module-name="相似商用素材"
                      data-search-word={searchWord}
                      rel="nofollow"
                    >
                      查看更多
                    </a>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function Index(props) {
  // 实验已下架，始终使用 default 组（原始版本）
  return <SimilarMaterialList {...props} />;
}

export default memo(Index);
